import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { todayMxDate } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Loader2,
  Clock,
  User,
  Phone,
  MapPin,
  Calendar as CalendarIcon,
  Wrench,
  DollarSign,
  MessageSquare,
  FileText,
  CheckCircle,
  Send,
  CalendarClock,
  Banknote,
  History
} from 'lucide-react';

const statusLabels = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_proceso: 'En Proceso',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
  reagendado: 'Reagendado',
  presupuesto_pendiente: 'Presup. Pendiente',
  presupuesto_aprobado: 'Presup. Aprobado'
};

export default function CalendarView() {
  const [currentDate, setCurrentDate] = useState(todayMxDate());
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayServices, setDayServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Finalize service state
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [serviceToFinalize, setServiceToFinalize] = useState(null);
  const [finalizeData, setFinalizeData] = useState({
    folio_nota: '',
    monto_cobrado: 0,
    comentario_cierre: '',
    enviar_a_presupuesto: false
  });
  const [finalizing, setFinalizing] = useState(false);

  // Reschedule service state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [serviceToReschedule, setServiceToReschedule] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    nueva_fecha: '',
    nueva_hora: '09:00',
    nuevo_abono: 0,
    comentario_reagendado: ''
  });
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    fetchTechnicians();
  }, []);

  useEffect(() => {
    fetchServices();
  }, [currentDate]);

  const fetchTechnicians = async () => {
    try {
      const res = await axios.get(`${API}/technicians?include_inactive=true`);
      setTechnicians(res.data);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const res = await axios.get(`${API}/calendar/services?year=${year}&month=${month}`);
      setServices(res.data);
    } catch (error) {
      toast.error('Error al cargar servicios del calendario');
    } finally {
      setLoading(false);
    }
  };

  // Filter services by selected technician
  const filteredServices = selectedTechnicianId === 'all' 
    ? services 
    : services.filter(s => s.technician_id === selectedTechnicianId);

  const selectedTechnician = technicians.find(t => t.id === selectedTechnicianId);

  const goToPreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(todayMxDate());
  };

  const getServicesForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const serviciosDelDia = [];
    
    filteredServices.forEach(s => {
      // Caso 1: El service_date actual es este día
      if (s.service_date === dayStr) {
        serviciosDelDia.push({
          ...s,
          _evento_tipo: 'programado', // Evento actualmente programado
          _evento_fecha: dayStr
        });
      }
      
      // Caso 2: Tiene eventos históricos en este día (reagendados, visitados, etc.)
      if (s.eventos && s.eventos.length > 0) {
        s.eventos.forEach(evento => {
          // Solo mostrar si el evento es de este día Y no es el evento activo actual
          if (evento.fecha_programada === dayStr && 
              evento.estatus_evento !== 'pendiente' && 
              s.service_date !== dayStr) {
            serviciosDelDia.push({
              ...s,
              // Sobrescribir datos con los del evento histórico
              _evento_tipo: 'historico',
              _evento_fecha: dayStr,
              _evento_estatus: 'visitado', // Siempre mostrar como "Visitado" para eventos históricos
              _evento_acudio: evento.acudio_domicilio,
              _evento_tecnico: evento.tecnico_nombre,
              _evento_horario: `${evento.hora_inicio || ''} - ${evento.hora_fin || ''}`,
              _evento_observaciones: evento.observaciones,
              _evento_anticipo: evento.anticipo_evento,
              _evento_movido_a: evento.nueva_fecha_programada,
              // Para mostrar en el calendario
              start_time: evento.hora_inicio,
              end_time: evento.hora_fin,
              technician_name: evento.tecnico_nombre
            });
          }
        });
      }
      
      // Caso 3: Tiene historial de asignaciones con fechas de este día (compatibilidad)
      if (!s.eventos?.length && s.historial_asignaciones && s.historial_asignaciones.length > 0) {
        s.historial_asignaciones.forEach(asig => {
          if (asig.fecha_anterior === dayStr && s.service_date !== dayStr) {
            serviciosDelDia.push({
              ...s,
              _evento_tipo: 'historico',
              _evento_fecha: dayStr,
              _evento_estatus: 'visitado', // Siempre mostrar como "Visitado" para eventos históricos
              _evento_acudio: asig.acudio_domicilio,
              _evento_tecnico: asig.tecnico_anterior_nombre,
              _evento_horario: asig.horario_anterior,
              _evento_observaciones: asig.observaciones_dia || asig.comentarios_visita_anterior,
              _evento_anticipo: asig.anticipo_registrado,
              _evento_movido_a: asig.fecha_servicio,
              start_time: asig.horario_anterior?.split(' - ')[0] || '',
              end_time: asig.horario_anterior?.split(' - ')[1] || '',
              technician_name: asig.tecnico_anterior_nombre
            });
          }
        });
      }
    });
    
    return serviciosDelDia.sort((a, b) => (a.start_time || a.service_time || '').localeCompare(b.start_time || b.service_time || ''));
  };

  const handleDayClick = (day) => {
    const dayServicesData = getServicesForDay(day);
    setSelectedDay(day);
    setDayServices(dayServicesData);
    setDialogOpen(true);
  };

  const handleTechnicianChange = (value) => {
    setSelectedTechnicianId(value);
  };

  const openFinalizeDialog = (service) => {
    setServiceToFinalize(service);
    setFinalizeData({
      folio_nota: '',
      monto_cobrado: service.quoted_cost || 0,
      comentario_cierre: '',
      enviar_a_presupuesto: false
    });
    setFinalizeDialogOpen(true);
  };

  const handleFinalizeService = async () => {
    if (!serviceToFinalize) return;
    
    if (!finalizeData.folio_nota.trim()) {
      toast.error('El folio de la nota es requerido');
      return;
    }

    setFinalizing(true);
    try {
      const response = await axios.post(`${API}/services/${serviceToFinalize.id}/finalize`, {
        folio_nota: finalizeData.folio_nota,
        monto_cobrado: parseFloat(finalizeData.monto_cobrado) || 0,
        comentario_cierre: finalizeData.comentario_cierre,
        enviar_a_presupuesto: finalizeData.enviar_a_presupuesto
      });
      
      toast.success('Servicio finalizado exitosamente');
      
      if (response.data.presupuesto_creado) {
        toast.success(`Presupuesto creado: ${response.data.presupuesto_creado.folio}`);
      }
      
      setFinalizeDialogOpen(false);
      setServiceToFinalize(null);
      fetchServices();
      // Update day services
      const updatedServices = dayServices.map(s => 
        s.id === serviceToFinalize.id ? { ...s, status: 'finalizado' } : s
      );
      setDayServices(updatedServices);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al finalizar servicio');
    } finally {
      setFinalizing(false);
    }
  };

  const handleSendToBudget = async (service) => {
    try {
      const response = await axios.post(`${API}/services/${service.id}/send-to-budget`);
      toast.success(`Presupuesto creado: ${response.data.budget.folio}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar a presupuestos');
    }
  };

  const openRescheduleDialog = (service) => {
    setServiceToReschedule(service);
    setRescheduleData({
      nueva_fecha: '',
      nueva_hora: service.service_time || '09:00',
      nuevo_abono: 0,
      comentario_reagendado: ''
    });
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleService = async () => {
    if (!serviceToReschedule) return;
    
    if (!rescheduleData.nueva_fecha) {
      toast.error('Selecciona una nueva fecha');
      return;
    }

    const montoTotal = serviceToReschedule.monto_total || serviceToReschedule.quoted_cost || 0;
    const anticipoActual = serviceToReschedule.anticipo || 0;
    const nuevoAnticipoTotal = anticipoActual + (parseFloat(rescheduleData.nuevo_abono) || 0);
    
    if (nuevoAnticipoTotal > montoTotal) {
      toast.error('El total de anticipos no puede exceder el monto total');
      return;
    }

    setRescheduling(true);
    try {
      const response = await axios.post(`${API}/services/${serviceToReschedule.id}/reschedule`, {
        nueva_fecha: rescheduleData.nueva_fecha,
        nueva_hora: rescheduleData.nueva_hora,
        nuevo_abono: parseFloat(rescheduleData.nuevo_abono) || 0,
        comentario_reagendado: rescheduleData.comentario_reagendado
      });
      
      toast.success('Servicio reagendado exitosamente');
      
      if (response.data.nuevo_abono > 0) {
        toast.success(`Nuevo abono registrado: $${response.data.nuevo_abono.toLocaleString()}`);
      }
      
      setRescheduleDialogOpen(false);
      setServiceToReschedule(null);
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reagendar');
    } finally {
      setRescheduling(false);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad the beginning with days from previous month
  const startDayOfWeek = monthStart.getDay();
  const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Calendario</h2>
          <p className="text-muted-foreground text-sm">
            {selectedTechnicianId === 'all' 
              ? 'Vista mensual de todos los servicios' 
              : `Agenda de ${selectedTechnician?.name || 'Técnico'}`
            }
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Technician Selector */}
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedTechnicianId} onValueChange={handleTechnicianChange}>
              <SelectTrigger className="w-[200px]" data-testid="technician-calendar-select">
                <SelectValue placeholder="Seleccionar técnico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los técnicos</SelectItem>
                {technicians.filter(t => t.is_active).map(tech => (
                  <SelectItem key={tech.id} value={tech.id}>
                    {tech.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={goToToday}>
              Hoy
            </Button>
            <Button variant="outline" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Technician Info Card */}
      {selectedTechnicianId !== 'all' && selectedTechnician && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Wrench className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{selectedTechnician.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedTechnician.specialty} • {selectedTechnician.phone}
                </p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-2xl font-bold text-primary">{filteredServices.length}</p>
                <p className="text-xs text-muted-foreground">servicios este mes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month Title */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xl capitalize text-center">
            {format(currentDate, 'MMMM yyyy', { locale: es })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-4">
          {/* Week Days Header */}
          <div className="grid grid-cols-7 mb-2">
            {weekDays.map((day) => (
              <div 
                key={day} 
                className="text-center text-xs sm:text-sm font-medium text-muted-foreground py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Padding days */}
            {Array.from({ length: paddingDays }).map((_, index) => (
              <div key={`pad-${index}`} className="min-h-[80px] sm:min-h-[100px]" />
            ))}

            {/* Days of month */}
            {daysInMonth.map((day) => {
              const dayServicesData = getServicesForDay(day);
              const isToday = isSameDay(day, todayMxDate());
              const hasServices = dayServicesData.length > 0;

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => handleDayClick(day)}
                  className={`
                    min-h-[80px] sm:min-h-[100px] p-1 border rounded-lg cursor-pointer
                    transition-colors hover:bg-muted/50
                    ${isToday ? 'border-primary bg-primary/5' : 'border-border'}
                  `}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <div className={`
                    text-sm font-medium mb-1 text-center
                    ${isToday ? 'text-primary' : ''}
                  `}>
                    {format(day, 'd')}
                  </div>
                  
                  {hasServices && (
                    <div className="space-y-1">
                      {dayServicesData.slice(0, 2).map((service, idx) => (
                        <div
                          key={`${service.id}-${idx}`}
                          className={`
                            ${service._evento_tipo === 'historico' 
                              ? 'bg-purple-100 dark:bg-purple-950/50 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700' 
                              : `status-${service.status}`
                            } 
                            text-xs p-1 rounded truncate
                          `}
                        >
                          <span className="hidden sm:inline">
                            {service.start_time || service.service_time}
                            {service.end_time && `-${service.end_time}`}
                            {' - '}
                          </span>
                          {service.client_name.split(' ')[0]}
                          {service._evento_tipo === 'historico' && (
                            <span className="ml-1 opacity-70">📋</span>
                          )}
                        </div>
                      ))}
                      {dayServicesData.length > 2 && (
                        <div className="text-xs text-muted-foreground text-center">
                          +{dayServicesData.length - 2} más
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded status-pendiente" />
              <span className="text-xs">Pendiente</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded status-confirmado" />
              <span className="text-xs">Confirmado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded status-en_proceso" />
              <span className="text-xs">En Proceso</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded status-finalizado" />
              <span className="text-xs">Finalizado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded status-cancelado" />
              <span className="text-xs">Cancelado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-purple-200 dark:bg-purple-800 border border-purple-400" />
              <span className="text-xs">Evento histórico (visitado/reagendado)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Day Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              {selectedDay && format(selectedDay, "EEEE, d 'de' MMMM", { locale: es })}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {dayServices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay servicios programados para este día</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dayServices
                  .sort((a, b) => (a.start_time || a.service_time || '').localeCompare(b.start_time || b.service_time || ''))
                  .map((service, idx) => (
                  <Card key={`${service.id}-${idx}`} className={`overflow-hidden ${service._evento_tipo === 'historico' ? 'border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-950/20' : ''}`}>
                    <CardContent className="p-4">
                      {/* Badge de evento histórico */}
                      {service._evento_tipo === 'historico' && (
                        <div className="mb-2 p-3 bg-purple-100 dark:bg-purple-900/50 rounded-lg text-sm border-l-4 border-purple-500">
                          <div className="flex items-center gap-2 text-purple-800 dark:text-purple-300 mb-2">
                            <History className="h-5 w-5" />
                            <span className="font-bold text-base">
                              Estado: VISITADO
                            </span>
                          </div>
                          
                          {/* Fechas claras */}
                          <div className="grid grid-cols-2 gap-2 mt-2 p-2 bg-white/50 dark:bg-black/20 rounded">
                            <div>
                              <span className="text-xs text-purple-600 dark:text-purple-400 font-medium block">📅 Fecha visitado:</span>
                              <span className="font-semibold text-purple-900 dark:text-purple-200">
                                {service._evento_fecha}
                              </span>
                            </div>
                            {service._evento_movido_a && (
                              <div>
                                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium block">🔄 Próxima visita:</span>
                                <span className="font-semibold text-blue-900 dark:text-blue-200">
                                  {service._evento_movido_a}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {service._evento_acudio && (
                            <p className="text-green-700 dark:text-green-400 mt-2 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" /> Técnico acudió al domicilio
                            </p>
                          )}
                          {service._evento_tecnico && (
                            <p className="text-purple-700 dark:text-purple-400 mt-1">
                              👷 Técnico que atendió: <span className="font-medium">{service._evento_tecnico}</span>
                            </p>
                          )}
                          {service._evento_anticipo > 0 && (
                            <p className="text-green-700 dark:text-green-400 mt-1 font-medium">
                              💵 Anticipo registrado ese día: ${service._evento_anticipo?.toLocaleString()}
                            </p>
                          )}
                          {service._evento_observaciones && (
                            <p className="text-purple-600 dark:text-purple-400 mt-2 italic bg-purple-50 dark:bg-purple-950/50 p-2 rounded">
                              📝 "{service._evento_observaciones}"
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{service.service_type}</h4>
                          <p className="text-sm text-muted-foreground">{service.folio}</p>
                        </div>
                        <Badge className={service._evento_tipo === 'historico' ? 'bg-purple-600 text-white' : `status-${service.status}`}>
                          {service._evento_tipo === 'historico' 
                            ? 'VISITADO'
                            : statusLabels[service.status]}
                        </Badge>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>
                            {service.start_time || service.service_time}
                            {service.end_time && ` - ${service.end_time}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{service.client_name}</span>
                        </div>
                        
                        {/* Teléfonos */}
                        {service.phones && service.phones.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{service.phones.join(', ')}</span>
                          </div>
                        )}
                        
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <span className="text-muted-foreground">{service.address}</span>
                        </div>
                        
                        {/* Descripción del servicio */}
                        {service.description && (
                          <div className="flex items-start gap-2 pt-2 border-t">
                            <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <span className="text-xs text-muted-foreground block">Descripción:</span>
                              <span className="text-sm">{service.description}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Comentarios internos */}
                        {service.internal_comments && (
                          <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded">
                            <MessageSquare className="h-4 w-4 text-amber-600 mt-0.5" />
                            <div>
                              <span className="text-xs text-amber-800 dark:text-amber-400 block font-medium">Comentarios:</span>
                              <span className="text-sm">{service.internal_comments}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Costo */}
                        {service.quoted_cost > 0 && (
                          <div className="flex items-center gap-2 pt-2 border-t">
                            <DollarSign className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-green-600">${service.quoted_cost?.toLocaleString()}</span>
                          </div>
                        )}

                        {/* Información de Pago (anticipo/restante) */}
                        {(service.anticipo > 0 || service.restante_por_pagar > 0) && (
                          <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded mt-2">
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground block">Total</span>
                                <span className="font-medium">${(service.monto_total || service.quoted_cost || 0).toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block">Anticipo</span>
                                <span className="font-medium text-green-600">${(service.anticipo || 0).toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block">Restante</span>
                                <span className={`font-medium ${(service.restante_por_pagar || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                  ${(service.restante_por_pagar || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {service.technician_name && (
                          <div className="pt-2 border-t">
                            <span className="text-muted-foreground">Técnico: </span>
                            <span className="font-medium">{service.technician_name}</span>
                          </div>
                        )}
                        {service.assigned_by && (
                          <div className="pt-2 border-t">
                            <span className="text-muted-foreground">Asignado por: </span>
                            <span className="font-medium">{service.assigned_by}</span>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        {service.status !== 'finalizado' && service.status !== 'cancelado' && (
                          <div className="space-y-2 pt-3 border-t">
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                onClick={() => openFinalizeDialog(service)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Finalizar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="flex-1"
                                onClick={() => handleSendToBudget(service)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                A Presupuesto
                              </Button>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                              onClick={() => openRescheduleDialog(service)}
                            >
                              <CalendarClock className="h-4 w-4 mr-1" />
                              Reagendar
                              {service.anticipo > 0 && (
                                <Badge variant="secondary" className="ml-2 text-xs">
                                  Anticipo: ${service.anticipo?.toLocaleString()}
                                </Badge>
                              )}
                            </Button>
                          </div>
                        )}
                        
                        {service.status === 'finalizado' && (
                          <div className="pt-3 border-t">
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              <span className="font-medium">Servicio Finalizado</span>
                            </div>
                            {service.folio_nota && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Folio: {service.folio_nota} | Cobrado: ${service.monto_cobrado?.toLocaleString() || 0}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Finalize Service Dialog */}
      <Dialog open={finalizeDialogOpen} onOpenChange={setFinalizeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Finalizar Servicio
            </DialogTitle>
            <DialogDescription>
              Ingresa los datos de cierre del servicio
            </DialogDescription>
          </DialogHeader>
          {serviceToFinalize && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm font-medium">{serviceToFinalize.folio}</p>
                    <p className="text-sm font-medium mt-1">{serviceToFinalize.client_name}</p>
                    <p className="text-xs text-muted-foreground">{serviceToFinalize.service_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Costo cotizado</p>
                    <p className="font-medium">${serviceToFinalize.quoted_cost?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="folio_nota_cal">Folio de la Nota *</Label>
                <Input
                  id="folio_nota_cal"
                  value={finalizeData.folio_nota}
                  onChange={(e) => setFinalizeData({ ...finalizeData, folio_nota: e.target.value })}
                  placeholder="Ej: 1234"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="monto_cobrado_cal">Monto Cobrado *</Label>
                <Input
                  id="monto_cobrado_cal"
                  type="number"
                  value={finalizeData.monto_cobrado}
                  onChange={(e) => setFinalizeData({ ...finalizeData, monto_cobrado: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="comentario_cierre_cal">Comentario de Cierre</Label>
                <Textarea
                  id="comentario_cierre_cal"
                  value={finalizeData.comentario_cierre}
                  onChange={(e) => setFinalizeData({ ...finalizeData, comentario_cierre: e.target.value })}
                  placeholder="Notas sobre el trabajo realizado"
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                <Checkbox
                  id="enviar_a_presupuesto_cal"
                  checked={finalizeData.enviar_a_presupuesto}
                  onCheckedChange={(checked) => setFinalizeData({ ...finalizeData, enviar_a_presupuesto: checked })}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="enviar_a_presupuesto_cal"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Enviar a Presupuestos
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Crear un presupuesto con los datos de este servicio
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setFinalizeDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleFinalizeService} 
                  disabled={finalizing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {finalizing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Finalizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirmar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Service Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-600" />
              Reagendar Servicio
            </DialogTitle>
            <DialogDescription>
              Cambia la fecha del servicio y agrega un abono si aplica
            </DialogDescription>
          </DialogHeader>
          {serviceToReschedule && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm font-medium">{serviceToReschedule.folio}</p>
                    <p className="text-sm font-medium mt-1">{serviceToReschedule.client_name}</p>
                    <p className="text-xs text-muted-foreground">{serviceToReschedule.service_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Fecha actual</p>
                    <p className="font-medium">{serviceToReschedule.service_date}</p>
                  </div>
                </div>
              </div>

              {/* Información de pago actual */}
              {(serviceToReschedule.anticipo > 0 || serviceToReschedule.monto_total > 0) && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <p className="text-xs font-medium text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Información de Pago Actual
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">Total</span>
                      <span className="font-medium">${(serviceToReschedule.monto_total || serviceToReschedule.quoted_cost || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Anticipo</span>
                      <span className="font-medium text-green-600">${(serviceToReschedule.anticipo || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Restante</span>
                      <span className="font-medium text-orange-600">${(serviceToReschedule.restante_por_pagar || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nueva Fecha *</Label>
                  <Input
                    type="date"
                    value={rescheduleData.nueva_fecha}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, nueva_fecha: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nueva Hora</Label>
                  <Input
                    type="time"
                    value={rescheduleData.nueva_hora}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, nueva_hora: e.target.value })}
                  />
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg space-y-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-400 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  ¿El cliente dio un nuevo abono?
                </p>
                <div className="space-y-2">
                  <Label className="text-xs">Nuevo Abono</Label>
                  <Input
                    type="number"
                    value={rescheduleData.nuevo_abono}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, nuevo_abono: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                {parseFloat(rescheduleData.nuevo_abono) > 0 && (
                  <div className="text-xs text-blue-800 dark:text-blue-400">
                    <p>Anticipo anterior: ${(serviceToReschedule.anticipo || 0).toLocaleString()}</p>
                    <p>Nuevo abono: ${parseFloat(rescheduleData.nuevo_abono || 0).toLocaleString()}</p>
                    <p className="font-medium">Total anticipos: ${((serviceToReschedule.anticipo || 0) + parseFloat(rescheduleData.nuevo_abono || 0)).toLocaleString()}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Motivo del Reagendado</Label>
                <Textarea
                  value={rescheduleData.comentario_reagendado}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, comentario_reagendado: e.target.value })}
                  placeholder="Ej: Cliente solicitó cambio de fecha..."
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleRescheduleService} 
                  disabled={rescheduling}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {rescheduling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Reagendando...
                    </>
                  ) : (
                    <>
                      <CalendarClock className="h-4 w-4 mr-2" />
                      Confirmar Reagendado
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
